import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

export default function BillingSection({ refresh }) {
  const [sub, setSub] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    api.get("/payments/subscription").then((r) => { setSub(r.data); if (r.data.ended) refresh(); }).catch(() => setSub({ error: true }));
  }, [refresh]);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(errText(err, "Couldn't open billing"));
      setOpening(false);
    }
  };

  if (!sub) return <p data-testid="billing-loading" className="text-sm text-muted-foreground">Loading billing…</p>;
  if (sub.error) return <p data-testid="billing-error" className="text-sm text-muted-foreground">Billing details aren't available right now.</p>;

  if (sub.plan === "free") {
    return (
      <div data-testid="billing-free" className="border border-border p-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Free plan</p>
          <p className="text-xs text-muted-foreground">3 documents · 10 MB per file</p>
        </div>
        <Link to="/#pricing" data-testid="billing-upgrade-link" className="text-sm text-primary underline underline-offset-4">Upgrade</Link>
      </div>
    );
  }

  if (sub.provider === "razorpay" || sub.provider === "paypal") {
    return (
      <div data-testid={`billing-${sub.provider}`} className="border border-border p-4 space-y-1">
        <p className="text-sm font-medium">{cap(sub.plan)} · {cap(sub.plan_interval)} <span className="font-mono-label text-muted-foreground ml-1">via {sub.provider === "paypal" ? "PayPal" : "Razorpay"}</span></p>
        <p className="text-xs text-muted-foreground">Prepaid until <strong>{day(sub.expires_at)}</strong>. Prepaid plans don't renew on their own, so there's nothing to cancel — buy another period from the pricing page any time.</p>
      </div>
    );
  }

  return (
    <div data-testid="billing-stripe" className="border border-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium">{cap(sub.plan)} · {cap(sub.plan_interval)}</p>
          <p data-testid="billing-renewal" className={`text-xs ${sub.cancel_at_period_end ? "text-destructive" : "text-muted-foreground"}`}>
            {sub.cancel_at_period_end ? `Cancels on ${day(sub.current_period_end)} — you keep access until then` : `Renews on ${day(sub.current_period_end)}`}
          </p>
        </div>
        {sub.card && (
          <span data-testid="billing-card" className="inline-flex items-center gap-1.5 font-mono-label border border-border px-2 py-1">
            <CreditCard className="w-3.5 h-3.5" /> {cap(sub.card.brand)} •••• {sub.card.last4}
          </span>
        )}
      </div>
      <Button data-testid="billing-portal-btn" onClick={openPortal} disabled={opening} variant="outline" className="rounded-sm border-foreground w-full">
        {opening ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
        {sub.cancel_at_period_end ? "Manage billing or resume plan" : "Manage billing"}
      </Button>
      <p className="text-xs text-muted-foreground">Cancel, change your card or download invoices on Stripe's secure page. You'll come straight back here.</p>
    </div>
  );
}
